import { AxiosResponse } from 'axios'
import {
  IMfaEnrollConfirmResponse,
  IMfaEnrollStartResponse,
  IMfaEnrollTotpStartResponse,
  IMfaMethodsResponse,
  IMfaRecoveryCodesResponse,
  IMfaResendLoginResponse,
  IMfaVerifyLoginRequest,
  TMfaMethodType,
  TMfaVerifyLoginResult
} from 'dtos/User'
import api from 'services/api'

// Nota 3 (portal-cannect-2fa) §2 — aliases de /authenticate_panel/mfa/verify
// e /mfa/resend: mesmo handler/service do manager, path próprio só por
// higiene de nome de rota para o consumidor portal.
export const verifyLoginChallenge = async (params: IMfaVerifyLoginRequest): Promise<TMfaVerifyLoginResult> => {
  const result: AxiosResponse<any> = await api.post('/authenticate/mfa/verify', params)
  const { data } = result

  if (data.purpose === 'mfa-recovery') {
    return { kind: 'recovery_grant', recoveryToken: data.recovery_token, expiresInSeconds: data.expires_in_seconds }
  }

  return { kind: 'authenticated', data }
}

export const resendLoginChallenge = async (challengeId: string): Promise<IMfaResendLoginResponse> => {
  const result: AxiosResponse<IMfaResendLoginResponse> = await api.post('/authenticate/mfa/resend', {
    challenge_id: challengeId
  })

  return result.data
}

// --- Gestão de 2FA (nota 3 §3 da feature 2fa-auth) — 100% reuso, sem
// mudança nenhuma no backend. CA14: os endpoints sensíveis pedem
// current_password no próprio body (step-up), sem token de step-up
// separado.

export const getMfaMethods = async (): Promise<IMfaMethodsResponse> => {
  const result: AxiosResponse<IMfaMethodsResponse> = await api.get('/mfa/methods')

  return result.data
}

export const removeMfaMethod = async (type: TMfaMethodType, currentPassword: string): Promise<void> => {
  await api.delete(`/mfa/methods/${type}`, { data: { current_password: currentPassword } })
}

export const regenerateRecoveryCodes = async (currentPassword: string): Promise<IMfaRecoveryCodesResponse> => {
  const result: AxiosResponse<IMfaRecoveryCodesResponse> = await api.post('/mfa/recovery-codes/regenerate', {
    current_password: currentPassword
  })

  return result.data
}

// enroll/{método} aceita `current_password` só quando o usuário já tem um
// método ativo (step-up de reconfiguração) — no primeiro enrollment forçado
// (MFA_ENROLLMENT_REQUIRED) o body vai vazio, o backend não exige senha.
//
// `/mfa/enroll/*` é chamado por 2 contextos que precisam de tratamento
// DIFERENTE de sessão expirada: o fluxo de login (useTwoFactorLoginFlow,
// token de enrollment de curta duração, redirect pro interceptor global
// atrapalharia o próprio fluxo — nota 3 §4/§8) e a tela autenticada de
// gestão (TwoFactorSettings, sessão completa normal, ONDE expirar/revogar
// deve continuar redirecionando pro login). Path sozinho não distingue os
// dois (achado do Lince, Gate D) — por isso `skipAuthRedirect` é opção
// explícita do CALLER, não inferida pela rota.
type TEnrollOptions = { skipAuthRedirect?: boolean }

export const enrollEmailStart = async (
  currentPassword?: string,
  options?: TEnrollOptions
): Promise<IMfaEnrollStartResponse> => {
  const result: AxiosResponse<IMfaEnrollStartResponse> = await api.post(
    '/mfa/enroll/email',
    { ...(currentPassword ? { current_password: currentPassword } : {}) },
    { skipAuthRedirect: options?.skipAuthRedirect }
  )

  return result.data
}

export const enrollEmailConfirm = async (
  enrollmentId: string,
  code: string,
  options?: TEnrollOptions
): Promise<IMfaEnrollConfirmResponse> => {
  const result: AxiosResponse<IMfaEnrollConfirmResponse> = await api.post(
    '/mfa/enroll/email/confirm',
    { enrollment_id: enrollmentId, code },
    { skipAuthRedirect: options?.skipAuthRedirect }
  )

  return result.data
}

export const enrollSmsStart = async (
  currentPassword?: string,
  options?: TEnrollOptions
): Promise<IMfaEnrollStartResponse> => {
  const result: AxiosResponse<IMfaEnrollStartResponse> = await api.post(
    '/mfa/enroll/sms',
    { ...(currentPassword ? { current_password: currentPassword } : {}) },
    { skipAuthRedirect: options?.skipAuthRedirect }
  )

  return result.data
}

export const enrollSmsConfirm = async (
  enrollmentId: string,
  code: string,
  options?: TEnrollOptions
): Promise<IMfaEnrollConfirmResponse> => {
  const result: AxiosResponse<IMfaEnrollConfirmResponse> = await api.post(
    '/mfa/enroll/sms/confirm',
    { enrollment_id: enrollmentId, code },
    { skipAuthRedirect: options?.skipAuthRedirect }
  )

  return result.data
}

export const enrollTotpStart = async (
  currentPassword?: string,
  options?: TEnrollOptions
): Promise<IMfaEnrollTotpStartResponse> => {
  const result: AxiosResponse<IMfaEnrollTotpStartResponse> = await api.post(
    '/mfa/enroll/totp',
    { ...(currentPassword ? { current_password: currentPassword } : {}) },
    { skipAuthRedirect: options?.skipAuthRedirect }
  )

  return result.data
}

export const enrollTotpConfirm = async (
  enrollmentId: string,
  code: string,
  options?: TEnrollOptions
): Promise<IMfaEnrollConfirmResponse> => {
  const result: AxiosResponse<IMfaEnrollConfirmResponse> = await api.post(
    '/mfa/enroll/totp/confirm',
    { enrollment_id: enrollmentId, code },
    { skipAuthRedirect: options?.skipAuthRedirect }
  )

  return result.data
}

export const startEnrollment = (method: TMfaMethodType, currentPassword?: string, options?: TEnrollOptions) => {
  if (method === 'email') return enrollEmailStart(currentPassword, options)
  if (method === 'sms') return enrollSmsStart(currentPassword, options)
  return enrollTotpStart(currentPassword, options)
}

export const confirmEnrollment = (
  method: TMfaMethodType,
  enrollmentId: string,
  code: string,
  options?: TEnrollOptions
) => {
  if (method === 'email') return enrollEmailConfirm(enrollmentId, code, options)
  if (method === 'sms') return enrollSmsConfirm(enrollmentId, code, options)
  return enrollTotpConfirm(enrollmentId, code, options)
}
